import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { Plus, Edit2, Trash2, X, Calendar, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import DateInput from '../components/ui/DateInput';
import { fmtDMY } from '../lib/dateFormat';

const SESSIONS = [
  { key: 1, label: 'S1 (10:00-11:30)' },
  { key: 2, label: 'S2 (11:30-13:00)' },
  { key: 3, label: 'S3 (13:30-15:00)' },
  { key: 4, label: 'S4 (15:00-16:30)' },
];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function toDateStr(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}
function addDays(dateStr, n) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + n);
  return toDateStr(d);
}
function isSecondSaturday(dateStr) {
  const d = new Date(dateStr);
  if (d.getDay() !== 6) return false; // not Saturday
  // Count how many Saturdays have passed in this month including this one
  const day = d.getDate();
  return day > 7 && day <= 14; // 2nd Saturday is between 8th and 14th
}
function isWeekend(dateStr) {
  const d = new Date(dateStr).getDay();
  return d === 0 || d === 6;
}
function getDiscipline(batchName) {
  const p = (batchName || '').substring(0, 2).toUpperCase();
  return ['EM', 'TM'].includes(p) ? 'mechanical' : ['EC', 'TC'].includes(p) ? 'civil' : null;
}
const TOPIC_COLORS = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#f97316','#84cc16','#ec4899','#14b8a6','#6366f1','#f43f5e'];


// All Batches horizontal timeline view
function AllBatchesView({ batches, supabase, refreshKey }) {
  const [allEntries, setAllEntries] = useState([]);
  const [allHolidays, setAllHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewYear, setViewYear] = useState(new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(new Date().getMonth());
  const activeBatches = batches.filter(b => b.status === 'ongoing' || b.status === 'upcoming');

  useEffect(() => { loadAll(); }, [viewMonth, viewYear, refreshKey]);

  async function toggleStatus(entry) {
    const next = entry.status === 'scheduled' ? 'completed' : entry.status === 'completed' ? 'cancelled' : 'scheduled';
    await supabase.from('schedule_entries').update({ status: next }).eq('id', entry.id);
    loadAll();
  }

  async function loadAll() {
    if (!activeBatches.length) return;
    setLoading(true);
    const startDate = toDateStr(new Date(viewYear, viewMonth, 1));
    const endDate = toDateStr(new Date(viewYear, viewMonth + 1, 0));
    const batchIds = activeBatches.map(b => b.id);
    const { data: ents } = await supabase.from('schedule_entries').select('*')
      .in('batch_id', batchIds).gte('scheduled_date', startDate).lte('scheduled_date', endDate).order('scheduled_date');
    const { data: hols } = await supabase.from('schedule_holidays').select('*')
      .gte('holiday_date', startDate).lte('holiday_date', endDate);
    setAllEntries(ents || []);
    setAllHolidays(hols || []);
    setLoading(false);
  }

  // Build days of month
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const days = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = toDateStr(new Date(viewYear, viewMonth, d));
    const dow = new Date(viewYear, viewMonth, d).getDay();
    const isHol = (allHolidays || []).some(h => h.holiday_date === dateStr && !h.batch_id);
    days.push({ d, dateStr, dow, isHol });
  }
  const holDates = new Set((allHolidays || []).filter(h => !h.batch_id).map(h => h.holiday_date));

  const TOPIC_COLORS = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#f97316','#84cc16','#ec4899','#14b8a6'];
  const topicColorMap = {};
  [...new Set(allEntries.map(e => e.topic_name))].forEach((t, i) => { topicColorMap[t] = TOPIC_COLORS[i % TOPIC_COLORS.length]; });

  return (
    <div className="card" style={{padding:20}}>
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16,flexWrap:'wrap'}}>
        <div style={{fontWeight:700,fontSize:15}}>All Batches Schedule</div>
        <div style={{display:'flex',alignItems:'center',gap:6}}>
          <button className="btn btn-ghost btn-sm" onClick={()=>{ if(viewMonth===0){setViewMonth(11);setViewYear(y=>y-1);}else setViewMonth(m=>m-1); }}><ChevronLeft size={14}/></button>
          <span style={{fontSize:13,fontWeight:600}}>{MONTHS[viewMonth]} {viewYear}</span>
          <button className="btn btn-ghost btn-sm" onClick={()=>{ if(viewMonth===11){setViewMonth(0);setViewYear(y=>y+1);}else setViewMonth(m=>m+1); }}><ChevronRight size={14}/></button>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={loadAll}>Refresh</button>
      </div>
      {loading ? <div style={{textAlign:'center',padding:40,color:'var(--text3)'}}>Loading...</div> : (
        <div style={{overflowX:'auto'}}>
          <table style={{borderCollapse:'collapse',minWidth:800}}>
            <thead>
              <tr>
                <th style={{padding:'6px 10px',textAlign:'left',background:'var(--surface2)',border:'1px solid var(--border)',minWidth:80,position:'sticky',left:0,zIndex:2,fontSize:12}}>Batch</th>
                {days.map(({d,dateStr,dow,isHol})=>(
                  <th key={dateStr} style={{padding:'3px 4px',textAlign:'center',background:isHol?'rgba(239,68,68,0.1)':dow===0?'rgba(0,0,0,0.05)':dow===6?'rgba(0,0,0,0.03)':'var(--surface2)',border:'1px solid var(--border)',minWidth:44,fontSize:10}}>
                    <div style={{color:dow===0?'var(--red)':dow===6?'#f97316':'var(--text3)',fontSize:9}}>{DAYS[dow]}</div>
                    <div style={{fontWeight:700,color:isHol?'var(--red)':'var(--text1)'}}>{d}</div>
                    {isHol&&<div style={{fontSize:8,color:'var(--red)'}}>Hol</div>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {activeBatches.map(batch=>{
                const batchHolDates = new Set([
                  ...holDates,
                  ...(allHolidays||[]).filter(h=>h.batch_id===batch.id).map(h=>h.holiday_date)
                ]);
                return (
                  <tr key={batch.id}>
                    <td style={{padding:'4px 10px',fontWeight:700,fontSize:12,border:'1px solid var(--border)',background:'var(--surface)',position:'sticky',left:0,zIndex:1}}>
                      {batch.batch_name}
                      <div style={{fontSize:9,color:'var(--text3)',fontWeight:400}}>{batch.status}</div>
                    </td>
                    {days.map(({dateStr,dow,isHol})=>{
                      const dayEntries = allEntries.filter(e=>e.batch_id===batch.id&&e.scheduled_date===dateStr);
                      const isBatchHol = batchHolDates.has(dateStr);
                      const isWeekend = dow===0||dow===6;
                      return (
                        <td key={dateStr} style={{padding:'2px 3px',border:'1px solid var(--border)',verticalAlign:'top',minWidth:44,background:isBatchHol?'rgba(239,68,68,0.08)':isWeekend?'rgba(0,0,0,0.03)':'var(--surface)'}}>
                          {dayEntries.map((e,i)=>(
                            <div key={e.id} title={`${e.topic_name}${e.faculty_names?' - '+e.faculty_names:''} [${e.status}] - Click to change status`}
                              onClick={()=>toggleStatus(e)}
                              style={{fontSize:10,borderRadius:3,padding:'2px 4px',marginBottom:1,background:(topicColorMap[e.topic_name]||'#3b82f6')+'22',color:e.status==='cancelled'?'#9ca3af':topicColorMap[e.topic_name]||'#3b82f6',overflow:'hidden',whiteSpace:'nowrap',textOverflow:'ellipsis',fontWeight:600,cursor:'pointer',textDecoration:e.status==='cancelled'?'line-through':'none',border:e.status==='completed'?'1.5px solid #10b981':e.status==='cancelled'?'1px dashed #9ca3af':'none',opacity:e.status==='cancelled'?0.6:1}}>
                              {e.status==='completed'?'✓ ':e.status==='cancelled'?'✕ ':''}{e.topic_name}
                              {e.faculty_names&&e.status!=='cancelled'&&<span style={{fontWeight:400,opacity:0.8,fontSize:7}}> {e.faculty_names.split(',')[0].trim()}</span>}
                            </div>
                          ))}
                          {isBatchHol&&!dayEntries.length&&<div style={{fontSize:7,color:'var(--red)',textAlign:'center'}}>off</div>}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{marginTop:8,display:'flex',gap:12,flexWrap:'wrap',fontSize:10,color:'var(--text3)'}}>
            {Object.entries(topicColorMap).map(([name,color])=>(
              <span key={name} style={{display:'flex',alignItems:'center',gap:4}}>
                <span style={{width:10,height:10,borderRadius:2,background:color+'33',display:'inline-block'}}/>
                {name}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ClassSchedule({ batches, topics, facultyList: facultyListProp }) {
  const [internalFacultyList, setInternalFacultyList] = useState(facultyListProp||[]);
  const [allBatchesEntries, setAllBatchesEntries] = useState([]);
  const [allBatchesRefreshKey, setAllBatchesRefreshKey] = useState(0);
  const [availBatch, setAvailBatch] = useState(''); // '' = all batches
  const [scheduleBatch, setScheduleBatch] = useState('');
  const [scheduleId, setScheduleId] = useState(null);
  const [entries, setEntries] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const today = new Date();
  const [calYear, setCalYear] = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth());
  const [selectedDay, setSelectedDay] = useState(null);
  const [activeView, setActiveView] = useState('calendar');
  const [availMonth, setAvailMonth] = useState(today.getMonth());
  const [availYear, setAvailYear] = useState(today.getFullYear());

  // Modals
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [entryForm, setEntryForm] = useState({ topic_id:'', topic_name:'', faculty_names:'', scheduled_date:'', sessions:[1,2], notes:'', status:'scheduled' });
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [holidayForm, setHolidayForm] = useState({ holiday_date:'', reason:'', batch_specific:false });
  const [showShiftModal, setShowShiftModal] = useState(false);
  const [shiftFrom, setShiftFrom] = useState('');
  const [shiftDays, setShiftDays] = useState(1);
  const [shiftBatchOnly, setShiftBatchOnly] = useState(true);
  const [showAutoModal, setShowAutoModal] = useState(false);
  const [showRepeatModal, setShowRepeatModal] = useState(false);
  const [repeatForm, setRepeatForm] = useState({ topic_name:'', faculty_name:'', from_date:'', to_date:'', sessions:[1,2], notes:'', student_ids:[] });
  const [repeatStudents, setRepeatStudents] = useState([]);
  const [repeatSaving, setRepeatSaving] = useState(false);
  const [repeatSearch, setRepeatSearch] = useState('');
  const [dragIdx, setDragIdx] = useState(null);
  const [autoStartDate, setAutoStartDate] = useState('');
  const [autoSkipWeekends, setAutoSkipWeekends] = useState(true);
  const [autoTopics, setAutoTopics] = useState([]);

  const batchObj = batches.find(b => b.batch_name === scheduleBatch);
  const discipline = getDiscipline(scheduleBatch);
  const filteredTopics = discipline ? topics.filter(t => t.discipline === discipline) : topics;
  const holidayDates = new Set(holidays.map(h => h.holiday_date));

  // Topic color map
  const topicColorMap = {};
  [...new Set(entries.map(e => e.topic_name))].forEach((t, i) => {
    topicColorMap[t] = TOPIC_COLORS[i % TOPIC_COLORS.length];
  });

  useEffect(() => {
    loadFacultyFromDB();
  }, []);

  async function loadAllBatchesEntries() {
    const activeBatchIds = batches.filter(b=>b.status==='ongoing'||b.status==='upcoming').map(b=>b.id);
    if (!activeBatchIds.length) return;
    const startDate = toDateStr(new Date(availYear, availMonth, 1));
    const endDate = toDateStr(new Date(availYear, availMonth+1, 0));
    const {data} = await supabase.from('schedule_entries').select('*')
      .in('batch_id', activeBatchIds)
      .gte('scheduled_date', startDate)
      .lte('scheduled_date', endDate);
    setAllBatchesEntries(data||[]);
  }

  async function loadFacultyFromDB() {
    const { data } = await supabase.from('hr_staff')
      .select('id, staff_name, designation')
      .eq('is_active', true)
      .in('designation', ['Head-Training','MEP Engineer','NDT Inspection Engineer','Project Engineer','Project Engineer-Mechanical','Project Manager-Civil','Sr. Inspection Engineer','Senior project Engineer-Civil','Project Engineer-Civil','External Faculty','Trainer','Faculty'])
      .order('staff_name');
    if (data?.length) setInternalFacultyList(data.map(f => f.staff_name));
  }

  useEffect(() => {
    if (scheduleBatch) loadSchedule();
    else { setScheduleId(null); setEntries([]); setHolidays([]); setSelectedDay(null); }
  }, [scheduleBatch]);

  useEffect(() => {
    loadAllBatchesEntries();
  }, [availMonth, availYear, batches.length]);

  useEffect(() => {
    if (batchObj?.start_date) {
      const d = new Date(batchObj.start_date);
      setCalYear(d.getFullYear());
      setCalMonth(d.getMonth());
    }
  }, [scheduleBatch]);

  async function loadSchedule() {
    setLoading(true);
    const bObj = batches.find(b => b.batch_name === scheduleBatch);
    if (!bObj) { setLoading(false); return; }
    let { data: sched } = await supabase.from('class_schedules').select('id').eq('batch_id', bObj.id).maybeSingle();
    if (!sched) {
      const { data: ns } = await supabase.from('class_schedules').insert({ batch_id: bObj.id, batch_name: scheduleBatch }).select('id').single();
      sched = ns;
    }
    setScheduleId(sched?.id);
    const { data: ents } = await supabase.from('schedule_entries').select('*').eq('batch_id', bObj.id).order('scheduled_date').order('sessions');
    setEntries(ents || []);
    const { data: hols } = await supabase.from('schedule_holidays').select('*').or(`batch_id.eq.${bObj.id},batch_id.is.null`).order('holiday_date');
    setHolidays(hols || []);
    setLoading(false);
  }

  async function saveEntry() {
    if (!entryForm.scheduled_date || !entryForm.topic_name) { toast.error('Date and topic required'); return; }
    setSaving(true);
    const bObj = batches.find(b => b.batch_name === scheduleBatch);
    const payload = {
      schedule_id: scheduleId, batch_id: bObj?.id,
      topic_id: entryForm.topic_id || null, topic_name: entryForm.topic_name,
      faculty_names: entryForm.faculty_names, scheduled_date: entryForm.scheduled_date,
      sessions: entryForm.sessions, num_sessions: entryForm.sessions.length,
      notes: entryForm.notes || null, status: entryForm.status || 'scheduled',
    };
    const { error } = editEntry
      ? await supabase.from('schedule_entries').update(payload).eq('id', editEntry.id)
      : await supabase.from('schedule_entries').insert(payload);
    if (error) toast.error(error.message);
    else { toast.success(editEntry ? 'Updated' : 'Entry added'); setShowEntryModal(false); loadSchedule(); }
    setSaving(false);
  }

  async function deleteEntry(id) {
    if (!window.confirm('Delete this entry?')) return;
    await supabase.from('schedule_entries').delete().eq('id', id);
    toast.success('Deleted'); loadSchedule();
  }

  async function saveHoliday() {
    const bObj = batches.find(b => b.batch_name === scheduleBatch);
    const { error } = await supabase.from('schedule_holidays').insert({
      holiday_date: holidayForm.holiday_date,
      reason: holidayForm.reason || null,
      batch_id: holidayForm.batch_specific ? bObj?.id : null,
    });
    if (error) { toast.error(error.message); return; }
    // Auto-shift: the new holiday is 1 working day.
    // ALL entries from affectedDate onwards shift forward by 1 working day each.
    const bObj2 = batches.find(b => b.batch_name === scheduleBatch);
    const affectedDate = holidayForm.holiday_date;
    if (!isWeekend(affectedDate)) {
      // Get all holidays including the new one just inserted
      const { data: updatedHols } = await supabase.from('schedule_holidays').select('holiday_date');
      const holSet = new Set((updatedHols||[]).map(h=>h.holiday_date));
      // Helper: given a date, find the next working day (skip weekends + holidays)
      function nextWorkingDay(dateStr) {
        let d = addDays(dateStr, 1);
        let safety = 0;
        while ((holSet.has(d) || isWeekend(d)) && safety < 60) { d = addDays(d, 1); safety++; }
        return d;
      }
      let shiftQ = supabase.from('schedule_entries').select('id,scheduled_date').gte('scheduled_date', affectedDate).order('scheduled_date');
      if (holidayForm.batch_specific && bObj2) shiftQ = shiftQ.eq('batch_id', bObj2.id);
      const { data: toShift2 } = await shiftQ;
      if (toShift2?.length) {
        // Each entry moves to next working day after its current scheduled date
        for (const e of toShift2) {
          const newDate = nextWorkingDay(e.scheduled_date);
          await supabase.from('schedule_entries').update({ scheduled_date: newDate }).eq('id', e.id);
        }
        toast.success(`Holiday added & shifted ${toShift2.length} entries to next working days`);
      } else {
        toast.success('Holiday added (no entries to shift)');
      }
    } else {
      toast.success('Holiday added (weekend - no shift needed)');
    }
    setShowHolidayModal(false); loadSchedule();
  }

  async function deleteHoliday(id) {
    await supabase.from('schedule_holidays').delete().eq('id', id);
    toast.success('Removed'); loadSchedule();
  }

  async function shiftSchedule() {
    if (!shiftFrom || !shiftDays) return;
    setSaving(true);
    const bObj = batches.find(b => b.batch_name === scheduleBatch);
    let q = supabase.from('schedule_entries').select('id,scheduled_date').gte('scheduled_date', shiftFrom);
    if (shiftBatchOnly && bObj) q = q.eq('batch_id', bObj.id);
    const { data: toShift } = await q;
    if (!toShift?.length) { toast.error('No entries from that date'); setSaving(false); return; }
    for (const e of toShift) {
      await supabase.from('schedule_entries').update({ scheduled_date: addDays(e.scheduled_date, shiftDays) }).eq('id', e.id);
    }
    toast.success(`Shifted ${toShift.length} entries by ${shiftDays} day(s)`);
    setShowShiftModal(false); loadSchedule(); setSaving(false);
  }

  async function autoGenerate() {
    if (!autoStartDate || !autoTopics.length) { toast.error('Select start date and topics'); return; }
    setSaving(true);
    const bObj = batches.find(b => b.batch_name === scheduleBatch);
    // Get or create schedule record fresh
    let sid = scheduleId;
    if (!sid && bObj) {
      let { data: sched } = await supabase.from('class_schedules').select('id').eq('batch_id', bObj.id).maybeSingle();
      if (!sched) {
        const { data: ns } = await supabase.from('class_schedules').insert({ batch_id: bObj.id, batch_name: scheduleBatch }).select('id').single();
        sched = ns;
      }
      sid = sched?.id;
      setScheduleId(sid);
    }
    let currentDate = autoStartDate;
    const toInsert = [];
    for (const t of autoTopics) {
      let daysLeft = Number(t.days) || 1;
      while (daysLeft > 0) {
        if (holidayDates.has(currentDate) || (autoSkipWeekends && isWeekend(currentDate))) {
          currentDate = addDays(currentDate, 1); continue;
        }
        toInsert.push({
          schedule_id: sid, batch_id: bObj?.id,
          topic_id: t.topic_id || null, topic_name: t.topic_name,
          faculty_names: t.faculty_names || '', scheduled_date: currentDate,
          sessions: t.sessions||[1,2], num_sessions: (t.sessions||[1,2]).length, status: 'scheduled',
        });
        daysLeft--;
        currentDate = addDays(currentDate, 1);
      }
    }
    const { error } = await supabase.from('schedule_entries').insert(toInsert);
    if (error) { toast.error('Insert error: ' + error.message); console.error(error); }
    else if (!toInsert.length) { toast.error('No entries to generate - check start date and topics'); }
    else { toast.success(`Generated ${toInsert.length} entries`); setShowAutoModal(false); loadSchedule(); }
    setSaving(false);
  }

  // Build calendar grid
  function buildCalendar() {
    const firstDay = new Date(calYear, calMonth, 1);
    const lastDay = new Date(calYear, calMonth + 1, 0);
    const startDow = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = toDateStr(new Date(calYear, calMonth, d));
      const dow = new Date(calYear, calMonth, d).getDay();
      cells.push({
        date: d, dateStr,
        dayEntries: entries.filter(e => e.scheduled_date === dateStr),
        isHol: holidayDates.has(dateStr),
        isWeekend: dow === 0 || dow === 6,
        dow,
      });
    }
    return cells;
  }

  const calCells = buildCalendar();
  const selectedDayEntries = selectedDay ? entries.filter(e => e.scheduled_date === selectedDay) : [];
  const selectedDayHoliday = selectedDay ? holidays.find(h => h.holiday_date === selectedDay) : null;

  // Faculty availability
  function buildAvailability() {
    const daysInMonth = new Date(availYear, availMonth + 1, 0).getDate();
    const workingDays = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = toDateStr(new Date(availYear, availMonth, d));
      const dow = new Date(availYear, availMonth, d).getDay();
      if (dow !== 0 && !holidayDates.has(dateStr)) {
        workingDays.push({ d, dateStr, dow, isSat: dow === 6, is2ndSat: isSecondSaturday(dateStr) });
      }
    }
    const sourceEntries = availBatch
      ? allBatchesEntries.filter(e=>e.batch_id===availBatch)
      : allBatchesEntries;
    const monthEntries = sourceEntries.filter(e => {
      const dt = new Date(e.scheduled_date);
      return dt.getFullYear() === availYear && dt.getMonth() === availMonth;
    });
    // Show all faculty from internalFacultyList + any in entries
    const facultyFromEntries = monthEntries.flatMap(e => (e.faculty_names || '').split(',').map(f => f.trim()).filter(Boolean));
    const facultyFromList = internalFacultyList.map(f => typeof f === 'string' ? f : f.staff_name).filter(Boolean);
    const allFaculty = [...new Set([...facultyFromList, ...facultyFromEntries])].sort();
    return { workingDays, monthEntries, allFaculty };
  }

  async function toggleEntryStatus(entry) {
    const next = entry.status === 'scheduled' ? 'completed' : entry.status === 'completed' ? 'cancelled' : 'scheduled';
    await supabase.from('schedule_entries').update({ status: next }).eq('id', entry.id);
    loadSchedule();
    setAllBatchesRefreshKey(k => k+1);
  }

  async function saveRepeatSession() {
    if (!repeatForm.topic_name || !repeatForm.from_date || !repeatForm.student_ids.length) {
      toast.error('Select topic, date and at least one student'); return;
    }
    setRepeatSaving(true);
    const bObj = batches.find(b => b.batch_name === scheduleBatch);
    const { error } = await supabase.from('repeat_sessions').insert({
      batch_id: bObj?.id, batch_name: scheduleBatch,
      topic_name: repeatForm.topic_name,
      faculty_name: repeatForm.faculty_name,
      session_date: repeatForm.from_date,
      to_date: repeatForm.to_date,
      sessions: repeatForm.sessions,
      student_ids: repeatForm.student_ids,
      notes: repeatForm.notes || null,
    });
    if (error) toast.error(error.message);
    else { toast.success(`Repeat session scheduled for ${repeatForm.student_ids.length} students`); setShowRepeatModal(false); }
    setRepeatSaving(false);
  }

  return (
    <div>
      {/* View toggle */}
      <div style={{display:'flex',gap:8,marginBottom:12}}>
        <button className={`btn btn-sm ${activeView==='calendar'?'btn-primary':'btn-secondary'}`} onClick={()=>setActiveView('calendar')}>Monthly Calendar</button>
        <button className={`btn btn-sm ${activeView==='faculty'?'btn-primary':'btn-secondary'}`} onClick={()=>setActiveView('faculty')}>Faculty Availability</button>
        <button className={`btn btn-sm ${activeView==='allbatches'?'btn-primary':'btn-secondary'}`} onClick={()=>setActiveView('allbatches')}>All Batches View</button>
      </div>

      {/* Batch selector + action buttons */}
      <div className="card" style={{padding:'12px 16px',marginBottom:12,display:'flex',gap:12,alignItems:'center',flexWrap:'wrap'}}>
        <select style={{padding:'4px 8px',fontSize:13,borderRadius:6,border:'1px solid var(--border)',width:220}} value={scheduleBatch} onChange={e=>setScheduleBatch(e.target.value)}>
          <option value="">Select Batch</option>
          {batches.filter(b=>b.status==='ongoing'||b.status==='upcoming').map(b=>(
            <option key={b.id} value={b.batch_name}>{b.batch_name} {b.status==='upcoming'?'(Upcoming)':''}</option>
          ))}
        </select>
        {scheduleBatch && (
          <>
            <span style={{fontSize:11,color:'var(--text3)'}}>{entries.length} entries</span>
            <div style={{display:'flex',gap:6,marginLeft:'auto',flexWrap:'wrap'}}>
              <button className="btn btn-secondary btn-sm" onClick={async()=>{ setAutoStartDate(entries.length>0 ? addDays(entries[entries.length-1].scheduled_date,1) : (batchObj?.start_date||''));
                // Try to copy sequence from existing same-discipline batch
                const disc = getDiscipline(scheduleBatch);
                const sameBatches = batches.filter(b=>b.batch_name!==scheduleBatch&&getDiscipline(b.batch_name)===disc);
                let seqTopics = null;
                // Only check the most recent same-discipline batch
                const recentBatch = sameBatches[sameBatches.length-1];
                if (recentBatch) {
                  const {data:existSeq} = await supabase.from('schedule_entries').select('topic_name,topic_id,faculty_names').eq('batch_id',recentBatch.id).order('scheduled_date').limit(200);
                  if (existSeq?.length) {
                    const seen=new Set(); const seq=[];
                    existSeq.forEach(e=>{ if(!seen.has(e.topic_name)){ seen.add(e.topic_name); const m=filteredTopics.find(t=>t.topic_name===e.topic_name); seq.push({topic_id:e.topic_id||m?.id||'',topic_name:e.topic_name,days:m?.duration_days||1,faculty_names:e.faculty_names||'',sessions:[1,2]}); } });
                    if(seq.length){ seqTopics=seq; toast.success(`Copied sequence from ${recentBatch.batch_name}`); }
                  }
                }
                if(!seqTopics) seqTopics=filteredTopics.map(t=>({topic_id:t.id,topic_name:t.topic_name,days:t.duration_days||1,faculty_names:'',sessions:[1,2]}));
                setAutoTopics(seqTopics); setShowAutoModal(true); }}>Auto Generate</button>
              <button className="btn btn-secondary btn-sm" onClick={()=>{ setShiftFrom(''); setShiftDays(1); setShowShiftModal(true); }}>Shift Schedule</button>
              <button className="btn btn-secondary btn-sm" onClick={()=>{ setHolidayForm({holiday_date:selectedDay||'',reason:'',batch_specific:false}); setShowHolidayModal(true); }}>Add Holiday</button>
              <button className="btn btn-primary btn-sm" onClick={()=>{ setEditEntry(null); setEntryForm({topic_id:'',topic_name:'',faculty_names:'',scheduled_date:selectedDay||'',sessions:[1,2],notes:'',status:'scheduled'}); setShowEntryModal(true); }}>
                <Plus size={13}/> Add Entry
              </button>
              <button className="btn btn-secondary btn-sm" onClick={async()=>{ 
                setRepeatForm({topic_name:'',faculty_name:'',session_date:selectedDay||'',sessions:[1,2],notes:'',student_ids:[]});
                setRepeatSearch('');
                const bObj=batches.find(b=>b.batch_name===scheduleBatch);
                if(bObj){const{data:stds}=await supabase.from('students').select('id,full_name,register_number').eq('batch_name',scheduleBatch).neq('is_active',false).order('full_name');setRepeatStudents(stds||[]);}
                setShowRepeatModal(true);
              }}>Repeat Session</button>
            </div>
          </>
        )}
      </div>

      {activeView === 'allbatches' ? (
        <AllBatchesView batches={batches} supabase={supabase} refreshKey={allBatchesRefreshKey} />
      ) : activeView === 'faculty' ? (
        // FACULTY AVAILABILITY - no batch required
        <div className="card" style={{padding:20}}>
          <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16,flexWrap:'wrap'}}>
            <div style={{fontWeight:700,fontSize:14}}>Faculty Availability {scheduleBatch ? `- ${scheduleBatch}` : ''}</div>
            <div style={{display:'flex',alignItems:'center',gap:6}}>
              <button className="btn btn-ghost btn-sm" onClick={()=>{ if(availMonth===0){setAvailMonth(11);setAvailYear(y=>y-1);}else setAvailMonth(m=>m-1); }}><ChevronLeft size={14}/></button>
              <span style={{fontSize:13,fontWeight:600}}>{MONTHS[availMonth]} {availYear}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>{ if(availMonth===11){setAvailMonth(0);setAvailYear(y=>y+1);}else setAvailMonth(m=>m+1); }}><ChevronRight size={14}/></button>
            </div>
            <select style={{fontSize:12,padding:'3px 8px',borderRadius:6,border:'1px solid var(--border)'}}
              value={availBatch} onChange={e=>setAvailBatch(e.target.value)}>
              <option value="">All Active Batches</option>
              {batches.filter(b=>b.status==='ongoing'||b.status==='upcoming').map(b=><option key={b.id} value={b.id}>{b.batch_name}</option>)}
            </select>
            <button className="btn btn-secondary btn-sm" onClick={()=>{ setHolidayForm({holiday_date:'',reason:'',batch_specific:false}); setShowHolidayModal(true); }}>+ Add Holiday</button>
          </div>
          {(()=>{
            const { workingDays, monthEntries, allFaculty } = buildAvailability();
            if (!allFaculty.length) return (<div style={{textAlign:'center',padding:40,color:'var(--text3)',fontSize:13}}>No faculty assigned in schedule entries for this month.</div>);
            return (
              <div style={{overflowX:'auto'}}>
                <table style={{borderCollapse:'collapse',fontSize:11,minWidth:500}}>
                  <thead><tr>
                    <th style={{padding:'6px 10px',textAlign:'left',background:'var(--surface2)',border:'1px solid var(--border)',minWidth:120,position:'sticky',left:0}}>Faculty</th>
                    {workingDays.map(({d,dateStr,dow,isSat,is2ndSat})=>(
                      <th key={dateStr} onClick={()=>{ setHolidayForm({holiday_date:dateStr,reason:'',batch_specific:false}); setShowHolidayModal(true); }} style={{padding:'3px 4px',textAlign:'center',background:isSat&&is2ndSat?'rgba(239,68,68,0.1)':isSat?'var(--surface2)':'var(--surface2)',border:'1px solid var(--border)',minWidth:32,cursor:'pointer',fontSize:10}}>
                        <div style={{color:is2ndSat?'var(--red)':'var(--text3)',fontSize:9}}>{['S','M','T','W','T','F','S'][dow]}</div>
                        <div style={{fontWeight:700,color:is2ndSat?'var(--red)':'inherit'}}>{d}{is2ndSat&&<span style={{fontSize:7,display:'block',color:'var(--red)'}}>2nd Sat</span>}</div>
                      </th>
                    ))}
                    <th style={{padding:'6px 8px',textAlign:'center',background:'var(--surface2)',border:'1px solid var(--border)',minWidth:60}}>Days</th>
                  </tr></thead>
                  <tbody>
                    {allFaculty.map(faculty=>{
                      const busyDays = workingDays.filter(({dateStr})=>monthEntries.some(e=>e.scheduled_date===dateStr&&(e.faculty_names||'').split(',').map(f=>f.trim()).includes(faculty)));
                      return (
                        <tr key={faculty}>
                          <td style={{padding:'4px 10px',fontWeight:600,border:'1px solid var(--border)',background:'var(--surface)',position:'sticky',left:0}}>{faculty}</td>
                          {workingDays.map(({dateStr,isSat,is2ndSat})=>{
                            const entry = monthEntries.find(e=>e.scheduled_date===dateStr&&(e.faculty_names||'').split(',').map(f=>f.trim()).includes(faculty));
                            return (<td key={dateStr} title={entry?entry.topic_name:'Available'} style={{padding:'2px 3px',textAlign:'center',border:'1px solid var(--border)',background:entry?'rgba(59,130,246,0.12)':is2ndSat?'rgba(239,68,68,0.06)':'rgba(16,185,129,0.06)',fontSize:9}}>
                              {entry?<span style={{color:'var(--accent)',fontWeight:700}}>{(entry.topic_name||'').substring(0,4)}</span>:<span style={{color:is2ndSat?'var(--red)':'var(--green)'}}>✓</span>}
                            </td>);
                          })}
                          <td style={{padding:'4px 8px',textAlign:'center',fontWeight:600,border:'1px solid var(--border)',background:'var(--surface2)',fontSize:11}}>{busyDays.length}/{workingDays.length}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot><tr>
                    <td style={{padding:'4px 10px',fontWeight:700,border:'1px solid var(--border)',background:'var(--surface2)',position:'sticky',left:0,fontSize:10}}>Free slots</td>
                    {workingDays.map(({dateStr,isSat,is2ndSat})=>{
                      const busy = monthEntries.filter(e=>e.scheduled_date===dateStr&&e.faculty_names).flatMap(e=>e.faculty_names.split(',').map(f=>f.trim()).filter(Boolean));
                      const free = allFaculty.filter(f=>!busy.includes(f)).length;
                      return <td key={dateStr} style={{padding:'2px 3px',textAlign:'center',border:'1px solid var(--border)',fontSize:10,background:is2ndSat?'rgba(239,68,68,0.05)':free>0?'rgba(16,185,129,0.08)':'transparent'}}><span style={{color:free>0?'var(--green)':'var(--text3)'}}>{free}</span></td>;
                    })}
                    <td style={{border:'1px solid var(--border)',background:'var(--surface2)'}}></td>
                  </tr></tfoot>
                </table>
              </div>
            );
          })()}
        </div>
      ) : !scheduleBatch ? (
        <div className="card" style={{padding:40}}>
          <div className="empty-state"><Calendar size={32}/><h3>Select a Batch</h3><p>Choose a batch to view or build its class schedule</p></div>
        </div>
      ) : loading ? (
        <div className="card" style={{padding:40,textAlign:'center',color:'var(--text3)'}}>Loading...</div>
      ) : !scheduleBatch ? (
        <div className="card" style={{padding:40}}>
          <div className="empty-state"><Calendar size={32}/><h3>Select a Batch</h3><p>Choose a batch to view or build its class schedule</p></div>
        </div>
      ) : loading ? (
        <div className="card" style={{padding:40,textAlign:'center',color:'var(--text3)'}}>Loading...</div>
      ) : (
        // CALENDAR VIEW
        <div style={{display:'flex',gap:16,alignItems:'flex-start',flexWrap:'wrap'}}>
          <div className="card" style={{padding:20,flex:'1 1 580px',minWidth:300}}>
            {/* Holidays */}
            {holidays.filter(h=>{ const d=new Date(h.holiday_date); return d.getFullYear()===calYear&&d.getMonth()===calMonth; }).length > 0 && (
              <div style={{marginBottom:10,display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
                <span style={{fontSize:11,fontWeight:600,color:'var(--red)'}}>Holidays this month:</span>
                {holidays.filter(h=>{ const d=new Date(h.holiday_date); return d.getFullYear()===calYear&&d.getMonth()===calMonth; }).map(h=>(
                  <span key={h.id} style={{fontSize:11,background:'rgba(239,68,68,0.1)',color:'var(--red)',padding:'2px 8px',borderRadius:12,display:'inline-flex',alignItems:'center',gap:4}}>
                    {new Date(h.holiday_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short'})}
                    {h.reason?` - ${h.reason}`:''}
                    <button style={{border:'none',background:'none',cursor:'pointer',color:'var(--red)',lineHeight:1,padding:0,fontSize:12}} onClick={()=>deleteHoliday(h.id)}>x</button>
                  </span>
                ))}
              </div>
            )}
            {/* Month nav */}
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
              <button className="btn btn-ghost btn-sm" onClick={()=>{ if(calMonth===0){setCalMonth(11);setCalYear(y=>y-1);}else setCalMonth(m=>m-1); }}><ChevronLeft size={16}/></button>
              <div style={{fontWeight:700,fontSize:14}}>{MONTHS[calMonth]} {calYear}</div>
              <button className="btn btn-ghost btn-sm" onClick={()=>{ if(calMonth===11){setCalMonth(0);setCalYear(y=>y+1);}else setCalMonth(m=>m+1); }}><ChevronRight size={16}/></button>
            </div>
            {/* Day headers */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:2,marginBottom:2}}>
              {DAYS.map((d,i)=>(
                <div key={d} style={{textAlign:'center',fontSize:10,fontWeight:700,color:(i===0||i===6)?'var(--red)':'var(--text3)',padding:'4px 0'}}>{d}</div>
              ))}
            </div>
            {/* Calendar cells */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:2}}>
              {calCells.map((cell,i)=>{
                if (!cell) return <div key={`e${i}`}/>;
                const isSelected = selectedDay === cell.dateStr;
                const isToday = cell.dateStr === toDateStr(new Date());
                return (
                  <div key={cell.dateStr} onClick={()=>setSelectedDay(isSelected?null:cell.dateStr)}
                    style={{
                      minHeight:65, borderRadius:6, padding:'3px 4px', cursor:'pointer',
                      background: isSelected?'var(--accent)': cell.isHol?'rgba(239,68,68,0.08)': cell.isWeekend?'rgba(0,0,0,0.03)':'var(--surface)',
                      border: isSelected?'2px solid var(--accent)': isToday?'2px solid var(--accent)':'1px solid var(--border)',
                      opacity: cell.isWeekend && !cell.dayEntries.length ? 0.4 : 1,
                    }}>
                    <div style={{fontSize:11,fontWeight:700,color:isSelected?'#fff':cell.isHol||cell.isWeekend?'var(--red)':'var(--text1)',marginBottom:2}}>{fmtDMY(cell.date)}</div>
                    {cell.isHol && <div style={{fontSize:8,color:isSelected?'rgba(255,255,255,0.8)':'var(--red)'}}>Holiday</div>}
                    {cell.dayEntries.slice(0,2).map(e=>(
                      <div key={e.id} style={{fontSize:8,borderRadius:3,padding:'1px 3px',marginBottom:1,background:isSelected?'rgba(255,255,255,0.25)':(topicColorMap[e.topic_name]||'#3b82f6')+'25',color:isSelected?'#fff':e.status==='cancelled'?'#9ca3af':(topicColorMap[e.topic_name]||'#3b82f6'),overflow:'hidden',whiteSpace:'nowrap',textOverflow:'ellipsis',border:e.status==='completed'?'1.5px solid #10b981':e.status==='cancelled'?'1px dashed #9ca3af':'none',opacity:e.status==='cancelled'?0.6:1}}>
                        <span style={{fontWeight:700,fontSize:9,textDecoration:e.status==='cancelled'?'line-through':'none'}}>{e.status==='completed'?'✓ ':''}{e.topic_name}</span>
                        {e.faculty_names && e.status!=='cancelled' && <span style={{opacity:0.8,fontSize:8}}> {e.faculty_names.split(',')[0].trim()}</span>}
                      </div>
                    ))}
                    {cell.dayEntries.length > 2 && <div style={{fontSize:8,color:isSelected?'rgba(255,255,255,0.7)':'var(--text3)'}}>+{cell.dayEntries.length-2}</div>}
                  </div>
                );
              })}
            </div>
            {/* Legend */}
            <div style={{marginTop:8,display:'flex',gap:12,flexWrap:'wrap',fontSize:10,color:'var(--text3)'}}>
              <span style={{display:'flex',alignItems:'center',gap:4}}><span style={{width:10,height:10,borderRadius:2,background:'rgba(239,68,68,0.15)',display:'inline-block'}}/>Holiday</span>
              <span style={{display:'flex',alignItems:'center',gap:4}}><span style={{width:10,height:10,borderRadius:2,border:'2px solid var(--accent)',display:'inline-block'}}/>Today / Selected</span>
            </div>
          </div>

          {/* Day detail panel */}
          {selectedDay && (
            <div className="card" style={{padding:20,flex:'0 0 300px',minWidth:260}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
                <div style={{fontWeight:700,fontSize:13}}>
                  {new Date(selectedDay).toLocaleDateString('en-IN',{weekday:'long',day:'2-digit',month:'short',year:'numeric'})}
                </div>
                <button className="btn btn-ghost btn-sm" onClick={()=>setSelectedDay(null)}><X size={14}/></button>
              </div>
              {selectedDayHoliday && (
                <div style={{background:'rgba(239,68,68,0.1)',border:'1px solid var(--red)',borderRadius:6,padding:'6px 10px',marginBottom:10,fontSize:12,color:'var(--red)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <span>Holiday: {selectedDayHoliday.reason||'Off Day'}</span>
                  <button style={{border:'none',background:'none',cursor:'pointer',color:'var(--red)',fontSize:14,lineHeight:1}} onClick={()=>deleteHoliday(selectedDayHoliday.id)}>x</button>
                </div>
              )}
              {selectedDayEntries.length === 0 ? (
                <div style={{textAlign:'center',padding:20,color:'var(--text3)',fontSize:12}}>No entries for this day.</div>
              ) : (
                <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:12}}>
                  {selectedDayEntries.map(e=>(
                    <div key={e.id} style={{border:`1px solid ${topicColorMap[e.topic_name]||'#3b82f6'}44`,borderLeft:`4px solid ${topicColorMap[e.topic_name]||'#3b82f6'}`,borderRadius:8,padding:'8px 10px',background:`${topicColorMap[e.topic_name]||'#3b82f6'}08`}}>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                        <div style={{fontWeight:700,fontSize:13,color:topicColorMap[e.topic_name]||'#3b82f6'}}>{e.topic_name}</div>
                        <div style={{display:'flex',gap:3}}>
                          <button className="btn btn-ghost btn-sm" style={{padding:'2px 4px'}} onClick={()=>{ setEditEntry(e); setEntryForm({topic_id:e.topic_id||'',topic_name:e.topic_name||'',faculty_names:e.faculty_names||'',scheduled_date:e.scheduled_date||'',sessions:e.sessions||[1,2],notes:e.notes||'',status:e.status||'scheduled'}); setShowEntryModal(true); }}><Edit2 size={11}/></button>
                          <button className="btn btn-ghost btn-sm" style={{padding:'2px 4px',color:e.status==='completed'?'var(--green)':e.status==='cancelled'?'var(--text3)':'var(--text2)',fontSize:10,fontWeight:600}} title={`Status: ${e.status} - Click to change`} onClick={()=>toggleEntryStatus(e)}>
                            {e.status==='completed'?'✓':e.status==='cancelled'?'✕':'○'}
                          </button>
                          <button className="btn btn-ghost btn-sm" style={{padding:'2px 4px',color:'var(--red)'}} onClick={()=>deleteEntry(e.id)}><Trash2 size={11}/></button>
                        </div>
                      </div>
                      {e.faculty_names && <div style={{fontSize:11,color:'var(--text2)',marginTop:2}}>Faculty: {e.faculty_names}</div>}
                      <div style={{fontSize:10,color:'var(--text3)',marginTop:2,display:'flex',alignItems:'center',gap:6}}>
                        <span>{Array.isArray(e.sessions)?e.sessions.map(s=>`S${s}`).join(' + '):e.sessions}</span>
                        <span style={{padding:'1px 6px',borderRadius:10,fontSize:9,fontWeight:600,background:e.status==='completed'?'rgba(16,185,129,0.15)':e.status==='cancelled'?'rgba(156,163,175,0.2)':'rgba(59,130,246,0.15)',color:e.status==='completed'?'#10b981':e.status==='cancelled'?'#9ca3af':'#3b82f6'}}>{e.status}</span>
                        <span style={{fontSize:9,color:'var(--text3)'}}>click ○✓✕ to change</span>
                      </div>
                      {e.notes && <div style={{fontSize:10,color:'var(--text3)',marginTop:2,fontStyle:'italic'}}>{e.notes}</div>}
                    </div>
                  ))}
                </div>
              )}
              <div style={{display:'flex',gap:6}}>
                <button className="btn btn-primary btn-sm" style={{flex:1}} onClick={()=>{ setEditEntry(null); setEntryForm({topic_id:'',topic_name:'',faculty_names:'',scheduled_date:selectedDay,sessions:[1,2],notes:'',status:'scheduled'}); setShowEntryModal(true); }}><Plus size={12}/> Add Entry</button>
                {!selectedDayHoliday && <button className="btn btn-secondary btn-sm" onClick={()=>{ setHolidayForm({holiday_date:selectedDay,reason:'',batch_specific:false}); setShowHolidayModal(true); }}>Holiday</button>}
              </div>
            </div>
          )}
        </div>
      )}

      {/* REPEAT SESSION MODAL */}
      {showRepeatModal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowRepeatModal(false)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <span className="modal-title">Schedule Repeat Session - {scheduleBatch}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowRepeatModal(false)}><X size={16}/></button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group"><label>From Date *</label><DateInput value={repeatForm.from_date} onChange={e=>setRepeatForm(f=>({...f,from_date:e.target.value}))}/></div>
                <div className="form-group"><label>To Date *</label><DateInput value={repeatForm.to_date} onChange={e=>setRepeatForm(f=>({...f,to_date:e.target.value}))}/></div>
                <div className="form-group"><label>Topic *</label>
                  <select value={repeatForm.topic_name} onChange={e=>setRepeatForm(f=>({...f,topic_name:e.target.value}))}>
                    <option value="">Select Topic</option>
                    {filteredTopics.map(t=><option key={t.id} value={t.topic_name}>{t.short_code?`${t.short_code} - ${t.topic_name}`:t.topic_name}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Faculty</label>
                  <select value={repeatForm.faculty_name} onChange={e=>setRepeatForm(f=>({...f,faculty_name:e.target.value}))}>
                    <option value="">Select Faculty</option>
                    {internalFacultyList.map((f,i)=><option key={i} value={typeof f==='string'?f:f.staff_name}>{typeof f==='string'?f:f.staff_name}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Sessions</label>
                  <div style={{display:'flex',gap:10,flexWrap:'wrap',marginTop:4}}>
                    {SESSIONS.map(s=>(
                      <label key={s.key} style={{display:'flex',alignItems:'center',gap:4,fontSize:12,cursor:'pointer'}}>
                        <input type="checkbox" checked={repeatForm.sessions.includes(s.key)} onChange={e=>setRepeatForm(f=>({...f,sessions:e.target.checked?[...f.sessions,s.key].sort():f.sessions.filter(x=>x!==s.key)}))}/>
                        {s.label}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="form-group" style={{gridColumn:'1/-1'}}><label>Notes</label><input type="text" value={repeatForm.notes} onChange={e=>setRepeatForm(f=>({...f,notes:e.target.value}))} placeholder="Reason for repeat session"/></div>
              </div>
              <div style={{marginTop:12}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                  <label style={{fontWeight:600,fontSize:13}}>Select Students ({repeatForm.student_ids.length}/{repeatStudents.length})</label>
                  <div style={{display:'flex',gap:6}}>
                    <button className="btn btn-ghost btn-sm" onClick={()=>setRepeatForm(f=>({...f,student_ids:repeatStudents.map(s=>s.id)}))}>Select All</button>
                    <button className="btn btn-ghost btn-sm" onClick={()=>setRepeatForm(f=>({...f,student_ids:[]}))}>Clear</button>
                  </div>
                </div>
                <input type="text" placeholder="Search student..." value={repeatSearch} onChange={e=>setRepeatSearch(e.target.value)}
                  style={{width:'100%',marginBottom:6,padding:'4px 8px',fontSize:12,borderRadius:6,border:'1px solid var(--border)'}}/>
                <div style={{maxHeight:200,overflowY:'auto',border:'1px solid var(--border)',borderRadius:6,padding:8,display:'flex',flexWrap:'wrap',gap:4}}>
                  {repeatStudents.filter(s=>!repeatSearch||s.full_name?.toLowerCase().includes(repeatSearch.toLowerCase())||s.register_number?.toLowerCase().includes(repeatSearch.toLowerCase())).map(s=>{
                    const sel = repeatForm.student_ids.includes(s.id);
                    return (
                      <button key={s.id} className={`btn btn-sm ${sel?'btn-primary':'btn-secondary'}`} style={{fontSize:11}}
                        onClick={()=>setRepeatForm(f=>({...f,student_ids:sel?f.student_ids.filter(x=>x!==s.id):[...f.student_ids,s.id]}))}>
                        {sel?'✓ ':''}{s.full_name} <span style={{opacity:0.7,fontSize:9}}>({s.register_number})</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={()=>setShowRepeatModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveRepeatSession} disabled={repeatSaving||!repeatForm.topic_name||!repeatForm.from_date||!repeatForm.student_ids.length}>
                {repeatSaving?'Saving...':'Schedule Repeat Session'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ENTRY MODAL */}
      {showEntryModal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowEntryModal(false)}>
          <div className="modal">
            <div className="modal-header"><span className="modal-title">{editEntry?'Edit Entry':'Add Entry'}</span><button className="btn btn-ghost btn-sm" onClick={()=>setShowEntryModal(false)}><X size={16}/></button></div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group"><label>Date *</label><DateInput value={entryForm.scheduled_date} onChange={e=>setEntryForm(f=>({...f,scheduled_date:e.target.value}))}/></div>
                <div className="form-group"><label>Topic *</label>
                  <select value={entryForm.topic_id} onChange={e=>{ const t=filteredTopics.find(x=>x.id===e.target.value); setEntryForm(f=>({...f,topic_id:e.target.value,topic_name:t?.topic_name||''})); }}>
                    <option value="">Select Topic</option>
                    {filteredTopics.map(t=><option key={t.id} value={t.id}>{t.short_code?`${t.short_code} - ${t.topic_name}`:t.topic_name}{t.duration_days?` (${t.duration_days} days)`:''}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{gridColumn:'1/-1'}}><label>Faculty</label>
                  <select value={entryForm.faculty_names} onChange={e=>setEntryForm(f=>({...f,faculty_names:e.target.value}))}>
                    <option value="">Select Faculty</option>
                    {internalFacultyList.map((f,i)=><option key={i} value={typeof f==='string'?f:f.staff_name}>{typeof f==='string'?f:f.staff_name}</option>)}
                  </select>
                  <input type="text" value={entryForm.faculty_names} onChange={e=>setEntryForm(f=>({...f,faculty_names:e.target.value}))} placeholder="Or type name(s), comma separated" style={{marginTop:4}}/>
                </div>
                <div className="form-group" style={{gridColumn:'1/-1'}}><label>Sessions</label>
                  <div style={{display:'flex',gap:12,flexWrap:'wrap',marginTop:4}}>
                    {SESSIONS.map(s=>(
                      <label key={s.key} style={{display:'flex',alignItems:'center',gap:4,fontSize:12,cursor:'pointer'}}>
                        <input type="checkbox" checked={entryForm.sessions.includes(s.key)} onChange={e=>setEntryForm(f=>({...f,sessions:e.target.checked?[...f.sessions,s.key].sort():f.sessions.filter(x=>x!==s.key)}))}/>
                        {s.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-group"><label>Notes</label><input type="text" value={entryForm.notes} onChange={e=>setEntryForm(f=>({...f,notes:e.target.value}))} placeholder="Optional"/></div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={()=>setShowEntryModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveEntry} disabled={saving}>{saving?'Saving...':'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* HOLIDAY MODAL */}
      {showHolidayModal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowHolidayModal(false)}>
          <div className="modal">
            <div className="modal-header"><span className="modal-title">Add Holiday</span><button className="btn btn-ghost btn-sm" onClick={()=>setShowHolidayModal(false)}><X size={16}/></button></div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group"><label>Date *</label><DateInput value={holidayForm.holiday_date} onChange={e=>setHolidayForm(f=>({...f,holiday_date:e.target.value}))}/></div>
                <div className="form-group"><label>Reason</label><input type="text" value={holidayForm.reason} onChange={e=>setHolidayForm(f=>({...f,reason:e.target.value}))} placeholder="e.g. Public Holiday"/></div>
                <div className="form-group" style={{gridColumn:'1/-1'}}>
                  <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer'}}>
                    <input type="checkbox" checked={holidayForm.batch_specific} onChange={e=>setHolidayForm(f=>({...f,batch_specific:e.target.checked}))}/>
                    Apply only to {scheduleBatch} (uncheck = all batches)
                  </label>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={()=>setShowHolidayModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveHoliday} disabled={!holidayForm.holiday_date}>Add</button>
            </div>
          </div>
        </div>
      )}

      {/* SHIFT MODAL */}
      {showShiftModal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowShiftModal(false)}>
          <div className="modal">
            <div className="modal-header"><span className="modal-title">Shift Schedule</span><button className="btn btn-ghost btn-sm" onClick={()=>setShowShiftModal(false)}><X size={16}/></button></div>
            <div className="modal-body">
              <div style={{background:'rgba(245,158,11,0.1)',border:'1px solid var(--amber)',borderRadius:8,padding:'10px 14px',marginBottom:12,fontSize:12,display:'flex',gap:8,alignItems:'flex-start'}}>
                <AlertTriangle size={16} style={{color:'var(--amber)',flexShrink:0,marginTop:1}}/>
                Shifts all entries from selected date onwards by N days.
              </div>
              <div className="form-grid">
                <div className="form-group"><label>Shift From Date *</label><DateInput value={shiftFrom} onChange={e=>setShiftFrom(e.target.value)}/></div>
                <div className="form-group"><label>Days to Shift *</label><input type="number" min={1} max={30} value={shiftDays} onChange={e=>setShiftDays(Number(e.target.value))}/></div>
                <div className="form-group" style={{gridColumn:'1/-1'}}>
                  <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer'}}>
                    <input type="checkbox" checked={shiftBatchOnly} onChange={e=>setShiftBatchOnly(e.target.checked)}/>
                    Only shift {scheduleBatch} (uncheck = all batches)
                  </label>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={()=>setShowShiftModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={shiftSchedule} disabled={saving||!shiftFrom}>{saving?'Shifting...':'Shift'}</button>
            </div>
          </div>
        </div>
      )}

      {/* AUTO GENERATE MODAL */}
      {showAutoModal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowAutoModal(false)}>
          <div className="modal modal-lg">
            <div className="modal-header"><span className="modal-title">Auto Generate - {scheduleBatch}</span><button className="btn btn-ghost btn-sm" onClick={()=>setShowAutoModal(false)}><X size={16}/></button></div>
            <div className="modal-body">
              <div style={{display:'flex',gap:16,marginBottom:12,flexWrap:'wrap',alignItems:'center'}}>
                <div className="form-group" style={{marginBottom:0}}><label>Start Date *</label><DateInput value={autoStartDate} onChange={e=>setAutoStartDate(e.target.value)}/></div>
                <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,cursor:'pointer',marginTop:20}}>
                  <input type="checkbox" checked={autoSkipWeekends} onChange={e=>setAutoSkipWeekends(e.target.checked)}/>
                  Skip Weekends (Sat and Sun)
                </label>
                <span style={{fontSize:11,color:'var(--text3)',marginTop:20}}>Total: {autoTopics.reduce((s,t)=>s+Number(t.days||0),0)} days</span>
              </div>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                <div style={{fontWeight:600,fontSize:13}}>Topic Sequence</div>
                <button className="btn btn-ghost btn-sm" style={{fontSize:11}} onClick={async()=>{
                  const bObj=batches.find(b=>b.batch_name===scheduleBatch);
                  if(!bObj) return;
                  const{data:existing}=await supabase.from('schedule_entries').select('topic_name,topic_id,faculty_names').eq('batch_id',bObj.id).order('scheduled_date');
                  // Group by topic+faculty sequence
                  const seen=[];
                  const seq=[];
                  (existing||[]).forEach(e=>{
                    const key=e.topic_name+'||'+(e.faculty_names||'');
                    if(!seen.includes(key)){seen.push(key);seq.push({topic_id:e.topic_id||'',topic_name:e.topic_name,days:1,faculty_names:e.faculty_names||''});}
                    else {const s=seq.find(x=>x.topic_name===e.topic_name&&x.faculty_names===(e.faculty_names||'')); if(s) s.days++;}
                  });
                  setAutoTopics(seq);
                  // Prefill start date from first entry
                  if(existing?.length) setAutoStartDate(existing[0].scheduled_date);
                  toast.success('Loaded existing schedule sequence');
                }}>Load from existing schedule</button>
              </div>
              <div style={{maxHeight:300,overflowY:'auto',border:'1px solid var(--border)',borderRadius:8}}>
                <table style={{width:'100%',borderCollapse:'collapse'}}>
                  <thead>
                    <tr style={{background:'var(--surface2)'}}>
                      <th style={{padding:'6px 8px',fontSize:11,textAlign:'left'}}>Topic</th>
                      <th style={{padding:'6px 8px',fontSize:11,textAlign:'center',width:70}}>Days <span style={{fontWeight:400,fontSize:9,color:'var(--text3)'}}>(from master)</span></th>
                      <th style={{padding:'6px 8px',fontSize:11}}>Faculty</th>
                      <th style={{padding:'6px 8px',fontSize:11,minWidth:200}}>Sessions</th>
                      <th style={{width:30}}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {autoTopics.map((t,i)=>(
                      <tr key={i} style={{borderTop:'1px solid var(--border)'}}>
                        <td style={{padding:'4px 8px',fontSize:12,fontWeight:600,display:'flex',alignItems:'center',gap:6}}>
                          <div style={{display:'flex',flexDirection:'column',gap:1}}>
                            <button style={{border:'none',background:'none',cursor:'pointer',fontSize:10,lineHeight:1,color:'var(--text3)',padding:'1px 3px'}} onClick={()=>{ if(i===0) return; setAutoTopics(prev=>{const a=[...prev];[a[i-1],a[i]]=[a[i],a[i-1]];return a;}); }} disabled={i===0}>▲</button>
                            <button style={{border:'none',background:'none',cursor:'pointer',fontSize:10,lineHeight:1,color:'var(--text3)',padding:'1px 3px'}} onClick={()=>{ if(i===autoTopics.length-1) return; setAutoTopics(prev=>{const a=[...prev];[a[i],a[i+1]]=[a[i+1],a[i]];return a;}); }} disabled={i===autoTopics.length-1}>▼</button>
                          </div>
                          {t.topic_name}
                        </td>
                        <td style={{padding:'4px 8px',textAlign:'center'}}>
                          <input type="number" min={1} max={60} value={t.days}
                            style={{width:50,textAlign:'center',fontSize:12,border:'1px solid var(--border)',borderRadius:4,padding:'2px 4px'}}
                            onChange={e=>setAutoTopics(prev=>prev.map((x,j)=>j===i?{...x,days:Number(e.target.value)}:x))}/>
                        </td>
                        <td style={{padding:'4px 8px'}}>
                          <select value={t.faculty_names||''} style={{width:'100%',fontSize:12,border:'1px solid var(--border)',borderRadius:4,padding:'2px 4px'}}
                            onChange={e=>setAutoTopics(prev=>prev.map((x,j)=>j===i?{...x,faculty_names:e.target.value}:x))}>
                            <option value="">Select Faculty</option>
                            {internalFacultyList.map((f,i)=><option key={i} value={typeof f==='string'?f:f.staff_name}>{typeof f==='string'?f:f.staff_name}</option>)}
                            {t.faculty_names&&!internalFacultyList.map(f=>typeof f==='string'?f:f.staff_name).includes(t.faculty_names)&&<option value={t.faculty_names}>{t.faculty_names}</option>}
                          </select>
                        </td>
                        <td style={{padding:'4px 6px'}}>
                          <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                            {SESSIONS.map(s=>(
                              <label key={s.key} style={{display:'flex',alignItems:'center',gap:2,fontSize:10,cursor:'pointer',whiteSpace:'nowrap'}}>
                                <input type="checkbox" checked={(t.sessions||[1,2]).includes(s.key)}
                                  onChange={e=>setAutoTopics(prev=>prev.map((x,j)=>j===i?{...x,sessions:e.target.checked?[...(x.sessions||[]),s.key].sort():(x.sessions||[]).filter(k=>k!==s.key)}:x))}/>
                                {s.label}
                              </label>
                            ))}
                          </div>
                        </td>
                        <td style={{padding:'4px 8px',textAlign:'center'}}>
                          <button style={{border:'none',background:'none',cursor:'pointer',color:'var(--red)',fontSize:16,lineHeight:1}} onClick={()=>setAutoTopics(prev=>prev.filter((_,j)=>j!==i))}>x</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{marginTop:8}}>
                <select style={{fontSize:12,padding:'3px 6px',borderRadius:4,border:'1px solid var(--border)'}}
                  onChange={e=>{ if (!e.target.value) return; const t=filteredTopics.find(x=>x.id===e.target.value); if(t) setAutoTopics(prev=>[...prev,{topic_id:t.id,topic_name:t.topic_name,days:t.duration_days||1,faculty_names:'',sessions:[1,2]}]); e.target.value=''; }}>
                  <option value="">+ Add topic to sequence</option>
                  {filteredTopics.map(t=><option key={t.id} value={t.id}>{t.short_code||t.topic_name}{t.duration_days?` (${t.duration_days} days)`:''}</option>)}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={()=>setShowAutoModal(false)}>Cancel</button>
              <button className="btn btn-secondary" onClick={async()=>{
                  if(!window.confirm('This will delete all existing entries for this batch and regenerate. Continue?')) return;
                  const bObj=batches.find(b=>b.batch_name===scheduleBatch);
                  if(bObj) await supabase.from('schedule_entries').delete().eq('batch_id',bObj.id);
                  autoGenerate();
                }} disabled={saving||!autoStartDate||!autoTopics.length}>Clear & Regenerate</button>
              <button className="btn btn-primary" onClick={autoGenerate} disabled={saving||!autoStartDate||!autoTopics.length}>{saving?'Appending...':'Append to Schedule'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
